import { FormArray, FormGroup } from '@angular/forms';

import type {
    PendingDropdownItem,
    RepeatableLogger,
    RepeatablePendingDropdownItemResolutionStats,
    RepeatablePendingDropdownResolutionStats,
    RepeatablePendingDropdownResolveOptions,
    RepeatablePendingDropdownResolutionTarget,
    RepeatableResolutionResult,
} from './repeatable-group.types';
import { RepeatableDropdownResolver } from './repeatable-dropdown.resolver';

export class RepeatablePendingDropdownResolver {
    public constructor(
        private readonly dropdownResolver: RepeatableDropdownResolver,
        private readonly logger: RepeatableLogger,
    ) {}

    public resolvePending(
        rowsArray: FormArray<FormGroup>,
        pendingDropdown: PendingDropdownItem[],
        options: RepeatablePendingDropdownResolveOptions = {},
    ): RepeatableResolutionResult {
        const targets = this.normalizeTargets(options.targets);
        const stillPending: PendingDropdownItem[] = [];
        const resolvedChanges: RepeatableResolutionResult['resolvedChanges'] = [];
        const itemStats: RepeatablePendingDropdownItemResolutionStats[] = [];
        let didResolveAny = false;
        let skippedItemCount = 0;

        for (const item of pendingDropdown) {
            if (!this.matchesTargets(item, targets)) {
                stillPending.push(item);
                skippedItemCount++;

                continue;
            }

            const rowGroup = rowsArray.at(item.rowIndex);
            const control = rowGroup?.get(item.logicalKey);

            if (!control) {
                this.logger.warn('tryResolvePending(): control missing.', { item });
                itemStats.push(this.createMissingControlStats(item));
                continue;
            }

            if (!item.isMulti) {
                const id = item.rawIds[0] ?? '';
                const selectedValue = this.dropdownResolver.mapIdToDropdownValue(id, item.sf, item.rowIndex);

                if (selectedValue !== null && selectedValue !== undefined) {
                    const previousValue = control.value;

                    control.patchValue(selectedValue, { emitEvent: false });
                    didResolveAny = true;
                    resolvedChanges.push({
                        rowIndex: item.rowIndex,
                        logicalKey: item.logicalKey,
                        previousValue,
                        currentValue: selectedValue,
                    });
                    itemStats.push(this.createItemStats(item, [id], [], 'resolved'));
                } else {
                    stillPending.push(item);
                    itemStats.push(this.createItemStats(item, [], [id], 'unresolved'));
                }

                continue;
            }

            const currentValue = Array.isArray(control.value) ? (control.value as unknown[]) : [];
            const resolvedIds: string[] = [];
            const unresolvedIds: string[] = [];
            const resolvedNow: unknown[] = [];

            for (const id of item.rawIds) {
                const selectedValue = this.dropdownResolver.mapIdToDropdownValue(id, item.sf, item.rowIndex);

                if (selectedValue !== null && selectedValue !== undefined) {
                    resolvedIds.push(id);
                    resolvedNow.push(selectedValue);
                } else {
                    unresolvedIds.push(id);
                }
            }

            if (resolvedNow.length > 0) {
                const previousValue = control.value;
                const nextValue = [...currentValue, ...resolvedNow];

                control.patchValue(nextValue, { emitEvent: false });
                didResolveAny = true;
                resolvedChanges.push({
                    rowIndex: item.rowIndex,
                    logicalKey: item.logicalKey,
                    previousValue,
                    currentValue: nextValue,
                });
            }

            if (unresolvedIds.length > 0) {
                stillPending.push({ ...item, rawIds: unresolvedIds });
            }

            itemStats.push(this.createItemStats(
                item,
                resolvedIds,
                unresolvedIds,
                this.resolveMultiStatus(resolvedIds, unresolvedIds),
            ));
        }

        return {
            stillPending,
            didResolveAny,
            resolvedChanges,
            stats: this.createResolutionStats(itemStats, skippedItemCount),
        };
    }


    private normalizeTargets(
        targets: readonly RepeatablePendingDropdownResolutionTarget[] | undefined,
    ): RepeatablePendingDropdownResolutionTarget[] {
        return (targets ?? [])
            .map((target) => ({
                ...(typeof target.logicalKey === 'string' && target.logicalKey.trim() !== ''
                    ? { logicalKey: target.logicalKey.trim() }
                    : {}),
                ...(Number.isInteger(target.rowIndex) && Number(target.rowIndex) >= 0
                    ? { rowIndex: Number(target.rowIndex) }
                    : {}),
            }))
            .filter((target) => target.logicalKey !== undefined || target.rowIndex !== undefined);
    }

    private matchesTargets(item: PendingDropdownItem, targets: readonly RepeatablePendingDropdownResolutionTarget[]): boolean {
        if (targets.length === 0) {
            return true;
        }

        return targets.some((target) => {
            const matchesLogicalKey = target.logicalKey === undefined || target.logicalKey === item.logicalKey;
            const matchesRowIndex = target.rowIndex === undefined || target.rowIndex === item.rowIndex;

            return matchesLogicalKey && matchesRowIndex;
        });
    }

    private createMissingControlStats(item: PendingDropdownItem): RepeatablePendingDropdownItemResolutionStats {
        return {
            rowIndex: item.rowIndex,
            logicalKey: item.logicalKey,
            rawIds: [...item.rawIds],
            resolvedIds: [],
            unresolvedIds: [...item.rawIds],
            optionCount: 0,
            isMulti: item.isMulti,
            status: 'missingControl',
        };
    }

    private createItemStats(
        item: PendingDropdownItem,
        resolvedIds: string[],
        unresolvedIds: string[],
        status: RepeatablePendingDropdownItemResolutionStats['status'],
    ): RepeatablePendingDropdownItemResolutionStats {
        return {
            rowIndex: item.rowIndex,
            logicalKey: item.logicalKey,
            rawIds: [...item.rawIds],
            resolvedIds: [...resolvedIds],
            unresolvedIds: [...unresolvedIds],
            optionCount: this.dropdownResolver.options(item.sf, item.rowIndex).length,
            isMulti: item.isMulti,
            status,
        };
    }

    private createResolutionStats(
        items: RepeatablePendingDropdownItemResolutionStats[],
        skippedItemCount: number,
    ): RepeatablePendingDropdownResolutionStats {
        return {
            attemptedItemCount: items.length,
            skippedItemCount,
            resolvedItemCount: items.filter((item) => item.status === 'resolved').length,
            partiallyResolvedItemCount: items.filter((item) => item.status === 'partiallyResolved').length,
            unresolvedItemCount: items.filter((item) => item.status === 'unresolved').length,
            missingControlCount: items.filter((item) => item.status === 'missingControl').length,
            resolvedValueCount: items.reduce((total, item) => total + item.resolvedIds.length, 0),
            unresolvedValueCount: items.reduce((total, item) => total + item.unresolvedIds.length, 0),
            items,
        };
    }

    private resolveMultiStatus(resolvedIds: string[], unresolvedIds: string[]): RepeatablePendingDropdownItemResolutionStats['status'] {
        if (unresolvedIds.length === 0) {
            return 'resolved';
        }

        return resolvedIds.length > 0 ? 'partiallyResolved' : 'unresolved';
    }
}
